<template>
	<div class="xs-12">
		<!-- Paragraphs -->
		<div>
			<div v-for="(paragraph, i) in paragraphs" :key="i" class="mb2">
				<!-- Paragraph name -->
				<div class="flex xs-between xs-middle bb1 bc7">
					<div class="flex xs-middle p2">
						<div class="c6 fs4 fw7 mr2">
							{{ $t("paragraph") }} {{ i + 1 }} :
						</div>
						<div class="c1 fs4 fw7">{{ paragraph.name || "---" }}</div>
						<div class="cu2" @click="openUpdateParagraphModal(paragraph)">
							<crm-icon name="edit" color="c2" />
						</div>
					</div>
					<div class="cu2 p1" @click="removeParagraph(paragraph)">
						<crm-icon color="cError" name="delete" />
					</div>
				</div>
				<!-- Toolbar -->
				<div class="xs-12 bb1 bc7 p2 pb0">
					<j-button
						v-for="(type, i) in sectionTypes"
						:key="i"
						type="tertiary"
						size="small"
						class="mr2 mb2"
						@click="addItem(paragraph.id, type)"
						>+ {{ type }}</j-button
					>
				</div>
				<!-- Items -->
				<div v-if="paragraph.items && paragraph.items.length">
					<div class="xs-12" v-for="(item, i) in paragraph.items" :key="i">
						<div class="xs-12">
							<div class="flex xs-between xs-middle">
								<div class="fs3 fw7 p2">{{ item.type }}</div>
								<!-- Actions -->
								<div
									class="cu2 flex p1 xs-center"
									@click="removeItem(paragraph.id, item)"
								>
									<crm-icon name="delete" color="cError" />
								</div>
							</div>
							<!-- Input component -->
							<div class="p2 pt0">
								<component
									:is="decideComponent(item.type)"
									size="small"
									:value="item.localizedContent || ''"
									:content="item.localizedContent || ' '"
									:courseId="resourceId"
									:lessonId="paragraph.id"
									:item="item"
									:inputId="item.id"
									:supportedFileTypes="
										item.type === 'IMAGE'
											? ['jpg', 'jpeg', 'png', 'bmp', 'gif']
											: item.type === 'VIDEO'
											? ['mp4']
											: []
									"
									:selectedLocale="selectedLocale"
									@blur="onBlur(item)"
									@input="onInput(item, $event)"
									@option:added="$emit('page:updated')"
									@option:updated="$emit('page:updated')"
									@option:removed="$emit('page:updated')"
								/>
							</div>
						</div>
					</div>
				</div>
				<div v-else class="flex xs-middle xs-center p2 fs3 c6">
					{{ $t("errors.no_data") }}
				</div>
			</div>
		</div>

		<div class="xs-12 p2 flex">
			<j-button size="small" class="mr2" @click="openAddParagraphModal"
				>+ {{ $t("add_paragraph") }}</j-button
			>
			<j-button size="small" type="error" @click="removePage">{{
				$t("delete_page")
			}}</j-button>
		</div>

		<!-- Input modal -->
		<core-modal name="inputModal" :headline="form.headline">
			<div class="p4">
				<div class="mb2 fw7">{{ $t("name") }}</div>
				<j-base-input-text class="mb8" v-model="form.name" />
				<j-button @click="form.action">{{ form.buttonTxt }}</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
/*
 * Read resources/readme.md to understand why we use courses API and what are the differences with Education module
 */
import JButton from "@/components/JButton"
import JCard from "@/components/JCard"
import JAccordion from "@/components/JAccordion"
import CrmIcon from "@/components/CrmIcon"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JInputList from "@/components/JInputList"
import PageEditorFileInput from "./PageEditorFileInput"
import PageEditorListInput from "./PageEditorListInput"
import CoreRichTextEditor from "@/modules/core/components/CoreRichTextEditor"
import CoreModal from "@/modules/core/components/CoreModal"
import { checkSuccess } from "../utils/helpers.js"
import api from "@/api/gql.js"
import queries from "../utils/queries.js"
import debounce from "lodash.debounce"

export default {
	components: {
		JButton,
		JCard,
		JAccordion,
		CrmIcon,
		JBaseInputLongText,
		JBaseInputText,
		JInputList,
		PageEditorFileInput,
		PageEditorListInput,
		CoreRichTextEditor,
		CoreModal
	},

	props: {
		index: Number,
		page: Object,
		resourceId: String,
		selectedLocale: String
	},

	data() {
		return {
			isLoading: false,
			sectionTypes: ["HEADLINE", "TEXT", "LIST", "IMAGE", "VIDEO", "HTML"],
			form: {
				name: null,
				key: null,
				buttonTxt: null,
				headline: null,
				action: null
			}
		}
	},

	computed: {
		paragraphs() {
			return this.page?.lessons || []
		}
	},

	methods: {
		/* We only update items content on blur, not on input */
		onBlur(item) {
			this.updateItem(item, item.localizedContent)
			this.$emit("page:updated")
		},

		/* For HTML items, we cannot mutate item directly or we have the jumping cursor bug
		 * Also we cannot don't have blur event with CoreRichTextEditor
		 * Also we cannot fetch or it will jump the cursor in the beginning
		 * For others, we set it here because if we use v-model it create the jumping cursor bug on HTML items, and it will be updated on blur
		 */
		onInput(item, value) {
			if (item.type === "HTML") {
				return debounce(async () => {
					this.updateItem(item, value)
				}, 200)()
			} else {
				item.localizedContent = value
			}
		},

		openAddParagraphModal() {
			this.form.name = null
			this.form.key = null
			this.form.buttonTxt = this.$t("add")
			this.form.headline = this.$t("add_paragraph")
			this.form.action = this.addParagraphToPage
			this.$modal.show("inputModal")
		},

		openUpdateParagraphModal(paragraph) {
			this.form.name = paragraph.name
			this.form.key = `lessons-${paragraph.id}-name`
			this.form.buttonTxt = this.$t("update")
			this.form.headline = this.$t("rename_paragraph")
			this.form.action = this.upsertL10n
			this.$modal.show("inputModal")
		},

		openUpdatePageModal(page) {
			this.form.name = page.name
			this.form.key = `sections-${page.id}-name`
			this.form.buttonTxt = this.$t("update")
			this.form.headline = this.$t("rename_page")
			this.form.action = this.upsertL10n
			this.$modal.show("inputModal")
		},

		decideComponent(type) {
			if (type === "HEADLINE") return "j-base-input-text"
			if (type === "TEXT") return "j-base-input-long-text"
			if (type === "LIST") return "page-editor-list-input"
			if (type === "IMAGE") return "page-editor-file-input"
			if (type === "VIDEO") return "page-editor-file-input"
			if (type === "HTML") return "core-rich-text-editor"
			return null
		},

		async removePage() {
			if (this.paragraphs.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.this_page_is_not_empty")
					}
				})
				return
			}
			let variables = {
				courseId: this.resourceId,
				sectionId: this.page.id
			}

			let res = await api({ query: queries.removeSectionFromCourse, variables })

			if (res?.data?.removeSectionFromCourse?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			/*
			 * Delete L10n
			 */
			variables = {
				key: `sections-${this.page.id}-name`
			}
			res = await api({ query: queries.deleteAllL10n, variables })
			this.$emit("page:removed")
		},

		async addParagraphToPage() {
			this.isLoading = true
			/*
			 * Add lesson to section
			 */
			let variables = {
				courseId: this.resourceId,
				sectionId: this.page.id,
				input: {}
			}

			let res = await api({ query: queries.addLessonToSection, variables })

			if (res?.data?.addLessonToSection?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			const createdLessonId = res.data.addLessonToSection.createdStatus?.id
			/*
			 * Upsert name in L10n table
			 */
			if (this.form?.name?.length) {
				variables = {
					l10n: {
						key: `lessons-${createdLessonId}-name`,
						locale: this.selectedLocale,
						value: this.form?.name
					}
				}

				res = await api({ query: queries.upsertL10n, variables })

				if (res?.data?.upsertL10n?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text: this.$t("errors.general")
						}
					})
					this.isLoading = false
					return
				}
			}
			this.isLoading = false
			this.$modal.hide(`inputModal`)
			this.$emit("page:updated")
		},

		async removeParagraph(paragraph) {
			if (paragraph?.items?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.this_paragraph_is_not_empty")
					}
				})
				return
			}
			/*
			 * Remove paragraph (lesson) from page (section)
			 */
			let variables = {
				courseId: this.resourceId,
				sectionId: this.page.id,
				lessonId: paragraph.id
			}

			let res = await api({ query: queries.removeLessonFromSection, variables })

			if (res?.data?.removeLessonFromSection?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			/*
			 * Delete l10n for lesson name
			 * TODO: Delete all localizedContent of items
			 */
			variables = {
				key: `lessons-${paragraph.id}-name`
			}
			res = await api({ query: queries.deleteAllL10n, variables })

			this.$emit("page:updated")
		},

		async upsertL10n() {
			this.isLoading = true
			const variables = {
				l10n: {
					key: this.form.key,
					locale: this.selectedLocale,
					value: this.form.name || ""
				}
			}

			const res = await api({ query: queries.upsertL10n, variables })
			this.isLoading = false
			if (
				!checkSuccess(res, "upsertL10n", this.$t("errors.general"), this.$store)
			)
				return
			this.$modal.hide(`inputModal`)
			this.$emit("page:updated")
		},

		async addItem(paragraphId, type) {
			if (this.isLoading) return
			this.isLoading = true
			let variables = {
				courseId: this.resourceId,
				lessonId: paragraphId,
				input: {
					type
				}
			}

			let res = await api({ query: queries.addLessonItem, variables })

			this.isLoading = false
			if (
				!checkSuccess(
					res,
					"addLessonItem",
					this.$t("errors.general"),
					this.$store
				)
			)
				return
			this.$emit("page:updated")
		},

		async updateItem(item, value) {
			if (this.isLoading) return
			if (!item.localizedContent) item.localizedContent = ""
			this.isLoading = true
			const variables = {
				l10n: {
					key: `lessonItems-${item.id}-content`,
					locale: this.selectedLocale,
					value: value || ""
				}
			}

			const res = await api({ query: queries.upsertL10n, variables })
			this.isLoading = false
			if (
				!checkSuccess(res, "upsertL10n", this.$t("errors.general"), this.$store)
			)
				return
		},

		async removeItem(paragraphId, item) {
			if (this.isLoading) return
			this.isLoading = true
			// Remove item from lesson
			let variables = {
				courseId: this.resourceId,
				lessonId: paragraphId,
				itemId: item.id
			}

			let res = await api({ query: queries.removeLessonItem, variables })

			if (
				!checkSuccess(
					res,
					"removeLessonItem",
					this.$t("errors.general"),
					this.$store
				)
			) {
				this.isLoading = false
				return
			}

			// Delete l10n localizedContent
			variables = {
				key: `lessonItems-${item.id}-content`
			}
			res = await api({ query: queries.deleteAllL10n, variables })

			// If QUIZZ or LIST, need to deleteAllL10n for all the options too
			if (item.type === "QUIZZ" || item.type === "LIST") {
				item.options.forEach(async opt => {
					variables = {
						key: `lessonItemsOptions-${opt.id}-content`
					}
					res = await api({ query: queries.deleteAllL10n, variables })
				})
			}
			this.isLoading = false
			this.$emit("page:updated")
		}
	}
}
</script>
